import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {FmlxTooltip} from 'fmlx-common-ui';
import _ from 'lodash';

import './MenuList.scss';

const Size = {
  SMALL: 'small',
  MEDIUM: 'medium',
  LARGE: 'large',
};

const Separator = React.memo(() => <div className="menu-list-separator" />);

const Gap = React.memo(() => <div className="menu-list-gap" />);

// #region Menu List Text
const MenuListText = React.memo(
  ({text, disabled, style}) => (
    <span className={`menu-list-text ${disabled && 'disabled'}`} style={style}>
      {text}
    </span>
  ),
  (prevProps, nextProps) =>
    prevProps.text === nextProps.text &&
    prevProps.disabled === nextProps.disabled &&
    _.isEqual(prevProps.style, nextProps.style),
);

MenuListText.propTypes = {
  text: PropTypes.string,
  disabled: PropTypes.bool,
  style: PropTypes.object,
};
MenuListText.defaultProps = {
  text: '',
  disabled: false,
  style: {},
};
// #endregion Menu List Text

// #region Menu List Item
const MenuListItem = React.memo(
  ({
    name,
    imgPath,
    isSelected,
    disabled,
    onClick,
    size,
    style,
    onExpand,
    children,
    menuRef,
    onMouseEnter,
    onMouseLeave,
    expandable,
    tooltipMessage,
  }) => {
    const [expandableHovered, setExpandableHovered] = useState(false);
    const hoverColor = '#CC6900';

    return (
      <FmlxTooltip title={tooltipMessage ?? ''} placement="bottom" id="menu-list-item-tooltip">
        {/* additional div to make tooltip works when disabled */}
        <div>
          <div
            ref={menuRef}
            className={`menu-list-item-wrapper ${isSelected ? 'selected' : ''} ${disabled ? 'disabled' : ''}`}
          >
            <div
              className={`menu-list-item${expandable ? '-expandable' : ''} ${size}`}
              style={style}
              onClick={onClick}
              role="presentation"
              onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave}
            >
              {!expandable && (
                <span style={{color: style?.color}} className="menu-list-item-name">
                  {name}
                </span>
              )}
              {expandable && (
                <div
                  style={expandableHovered ? {backgroundColor: hoverColor, color: style?.color} : {color: style?.color}}
                  className="menu-list-item-expandable-name"
                  onMouseEnter={() => {
                    if (!isSelected) setExpandableHovered(true);
                  }}
                  onMouseLeave={() => setExpandableHovered(false)}
                >
                  {name}
                </div>
              )}
              {imgPath && <img className="menu-list-item-img" alt={name} src={imgPath} />}
              {expandable && (
                <div
                  className={`menu-list-item${expandable ? '-expandable' : ''}-arrow`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onExpand();
                  }}
                  role="presentation"
                  style={expandableHovered ? {backgroundColor: hoverColor} : {}}
                  onMouseEnter={() => {
                    if (!isSelected) setExpandableHovered(true);
                  }}
                  onMouseLeave={() => setExpandableHovered(false)}
                >
                  <img
                    alt={`${name}-dropdown`}
                    // eslint-disable-next-line
                      src={`./svg/protocol-editor/triangle-bottom${isSelected ? '-selected' : disabled ? '-disabled' : ''}.svg`}
                  />
                </div>
              )}
            </div>
            {children}
          </div>
        </div>
      </FmlxTooltip>
    );
  },
  (prevProps, nextProps) =>
    prevProps.name === nextProps.name &&
    prevProps.imgPath === nextProps.imgPath &&
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.disabled === nextProps.disabled &&
    prevProps.size === nextProps.size &&
    prevProps.expandable === nextProps.expandable &&
    prevProps.onClick === nextProps.onClick &&
    _.isEqual(prevProps.style, nextProps.style),
);
MenuListItem.propTypes = {
  name: PropTypes.any.isRequired,
  imgPath: PropTypes.string,
  isSelected: PropTypes.bool,
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
  size: PropTypes.string,
  style: PropTypes.object,
  onExpand: PropTypes.func,
  children: PropTypes.any,
  menuRef: PropTypes.any,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  expandable: PropTypes.bool,
  tooltipMessage: PropTypes.string,
};
MenuListItem.defaultProps = {
  isSelected: false,
  disabled: false,
  onClick: () => {
    console.log('pipetting channel clicked');
  },
  size: Size.SMALL,
  style: {},
  onExpand: () => {},
  children: null,
  menuRef: null,
  onMouseEnter: () => {},
  onMouseLeave: () => {},
  expandable: false,
  imgPath: '',
  tooltipMessage: '',
};
// #endregion Menu List Item

function MenuList({children}) {
  return (
    <div className="menu-list">
      <div className="menu-list-content">{children}</div>
    </div>
  );
}

MenuList.propTypes = {
  children: PropTypes.any.isRequired,
};

MenuList.Size = Size;

MenuList.Separator = Separator;
MenuList.Text = MenuListText;
MenuList.Item = MenuListItem;
MenuList.Gap = Gap;

export default MenuList;
